import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Button, Grid, Tab, Tabs } from '@mui/material';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import {
  useOperatorsVehiclesQuery,
  useDeleteOperatorsVehiclesMutation,
} from 'api/queries/operators';
import { toast } from 'react-hot-toast';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import ComposedDrawerMenuAppBar from 'components/ComposedDrawerMenuAppBar';
import { LogoElement, UserPermission, ConfirmModal } from 'remote/global';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, Link as RouterLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils/data/routes';
import { hasWritePermission } from 'utils/helpers';
import RepairModal from 'components/modals/VehicleRepairModal';

function VehicleDetailsView() {
  const [isRepairModalOpen, setIsRepairModalOpen] = useState(false);
  const [isDeleteVehicleModalOpen, setIsDeleteVehicleModalOpen] = useState(false);
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const tInstance = useTranslation();
  const navigate = useNavigate();
  const { t } = tInstance;
  const { id } = useParams();
  const { data: vehicle, isSuccess } = useOperatorsVehiclesQuery(id!);
  const { permissions = {} } = userInfo ?? {};
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const location = useLocation();
  const currentTab = location.pathname.split('/').pop();
  const deleteOperatorsVehiclesMutation = useDeleteOperatorsVehiclesMutation(id!);

  const handleClearRepairModalState = () => {
    setIsRepairModalOpen(false);
  };

  const handleDeleteVehicle = async () => {
    try {
      await deleteOperatorsVehiclesMutation.mutateAsync(id!);
      navigate([ROUTES.BaseRoutes.MAIN, ROUTES.MainRoutes.FLEET].join('/'), {
        replace: true,
      });
      toast.success(t('Toast.success'));
    } catch (error) {
      // TODO: Handle error
    }
  };

  if (!isSuccess) return null;

  // This needs to be available before component renders because logout>login causes crash
  // TODO: Handle this differently so that the component render tree doesn't have to pause
  if (!googleConfig?.feApiKey) return null;

  return (
    <>
      <ConfirmModal
        open={isDeleteVehicleModalOpen}
        setOpen={setIsDeleteVehicleModalOpen}
        onClose={() => setIsDeleteVehicleModalOpen(false)}
        onConfirm={handleDeleteVehicle}
        description={t('modals.deleteVehicle.subtitle', {
          licensePlate: vehicle.licensePlate,
        }).toString()}
      />
      <RepairModal
        open={isRepairModalOpen}
        setOpen={setIsRepairModalOpen}
        onClose={handleClearRepairModalState}
        defaultValues={{
          ownerId: Number(vehicle.id) ?? '',
          resourceId: Number(id),
        }}
      />
      <ComposedDrawerMenuAppBar
        title={
          vehicle != null
            ? `${vehicle.licensePlate} (${t(`VehicleClass.${vehicle.vehicleClass}`)})`
            : ''
        }
      />
      <Box sx={{ p: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Grid container gap={1}>
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              variant='outlined'
              onClick={() => setIsRepairModalOpen(true)}
              startIcon={<Add />}
              disabled={!hasWritePermission(permissions?.VEHICLE_SCHEDULE)}
            >
              {t('FleetView.actions.addVehicleToRepairLog')}
            </Button>
            <Button
              variant='outlined'
              onClick={() => setIsDeleteVehicleModalOpen(true)}
              startIcon={<DeleteOutline />}
              disabled={!hasWritePermission(permissions?.VEHICLE_SCHEDULE)}
            >
              {t('FleetView.actions.deleteVehicle')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Tabs variant='scrollable' value={currentTab}>
              <Tab
                label={t('FleetView.DetailView.tabs.details')}
                component={RouterLink}
                to=''
                value={id}
              />
              <Tab
                label={t('FleetView.DetailView.tabs.location')}
                component={RouterLink}
                to={ROUTES.FleetRoutes.LOCATION}
                value={ROUTES.FleetRoutes.LOCATION}
              />
              {[UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
                permissions?.VEHICLE_FILE as UserPermission
              ) ? null : (
                <Tab
                  label={t('FleetView.DetailView.tabs.documents')}
                  component={RouterLink}
                  to={ROUTES.FleetRoutes.DOCUMENTS}
                  value={ROUTES.FleetRoutes.DOCUMENTS}
                />
              )}
              {[UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(
                permissions?.VEHICLE_SCHEDULE
              ) ? null : (
                <Tab
                  label={t('FleetView.DetailView.tabs.repairLog')}
                  component={RouterLink}
                  to={ROUTES.FleetRoutes.REPAIR}
                  value={ROUTES.FleetRoutes.REPAIR}
                />
              )}
            </Tabs>
          </Grid>
        </Grid>
        <LogoElement
          color='blue'
          sx={{
            pointerEvents: 'none',
            position: 'fixed',
            right: '-126px',
            transform: 'rotate(12deg)',
          }}
        />
        <Outlet />
      </Box>
    </>
  );
}

export default VehicleDetailsView;
